import { isEmpty, isNil, keys } from "lodash";
import type {
  DefaultSet,
  DocumentAccessData,
  DocumentField,
  FreeFormatFilesContainer,
  ReferencedSet,
  SelectedDocumentField,
  SelectedSet,
  SelectedSetField,
  SetContainer,
  SetId,
  TypedSet,
} from "./document-selection.model";
import { DocumentSetGroupsSettings } from "./document-set-group-settings";
import type { DocumentType, DocumentTypeUploadRestriction, TypedSetDocumentType } from "./document-set-settings";
import {
  DocumentSetSettings,
  DocumentTypeUploadRestrictions,
  SystemManagedDocumentTypes,
} from "./document-set-settings";
import type { DocumentSetGroupArray, DocumentSetType, DocumentSetTypes } from "./document-set-settings.model";
import type { DocumentSetSyncConfig } from "./document-set-sync-config";
import type { CombinedDocumentContractType } from "./document-set-sync-settings";
import { DocumentTypeSynchronizedSetFields } from "./document-set-sync-settings";
import type { DocumentFileModel, DocumentSetModel } from "./model/document.model";
import type { IStaticDataModel } from "./model/staticData.model";
import type { nil } from "./type-utils";

export function isSelectedSetField(field: SelectedDocumentField): field is SelectedSetField {
  return typeof (field as SelectedSetField)?.field === "string";
}

export function isDefaultSet(set: SelectedSet): set is DefaultSet {
  return set === "Default";
}

export function isSetId(set: SelectedSet): set is SetId {
  return typeof (set as SetId)?.id === "string";
}

export function isTypedSet(set: SelectedSet): set is TypedSet {
  return typeof (set as TypedSet)?.documentSetType === "string";
}

export function isReferencedSet(set: SelectedSet): set is ReferencedSet {
  return typeof (set as ReferencedSet)?.foreignKey === "string";
}

export function isSelectedDocumentField(field: SelectedDocumentField): field is DocumentField {
  return !isSelectedSetField(field);
}

export function getForeignKey(selectedField: SelectedDocumentField): string | null {
  return isSelectedSetField(selectedField) && isReferencedSet(selectedField.selectedSet)
    ? selectedField.selectedSet.foreignKey
    : null;
}

// Returning an empty array for absent sets is intended, not a fallback to work around: callers treat
// "no sets" and "empty sets" alike. The same holds for getSetFiles and getFiles below.
export function getSets<T extends SetContainer>(document: T): T["documentSets"] {
  return document?.documentSets ?? [];
}

export function hasSetFiles<T extends DocumentSetModel>(set: T): boolean {
  return getSetFiles(set)?.length > 0;
}

export function getSetFiles<T extends DocumentSetModel>(set: T): T["files"] {
  return set?.files ?? [];
}

export function setHasDates<T extends DocumentSetModel>(set: T): boolean {
  return !!set?.issueDate || !!set?.dateOfReceipt || !!set?.validFrom || !!set?.validUntil;
}

export function getSetPhysicalTypes<T extends DocumentSetModel>(documentSet: T): string[] {
  return documentSet?.physicalTypes ?? [];
}

export function getDefaultSet<T extends SetContainer>(document: T): T["documentSets"][number] {
  return getSets(document).find((set) => set.isDefaultSet);
}

export function getSetById<T extends SetContainer>(document: T, setId: string): T["documentSets"][number] {
  return getSets(document).find((set) => set.id === setId);
}

export function getSetByType<T extends SetContainer>(
  document: T,
  documentSetType: DocumentSetType
): T["documentSets"][number] {
  return getSets(document).find((set) => set.type === documentSetType);
}

export function getReferencedSet<T extends SetContainer>(document: T, foreignKey: string): T["documentSets"][number] {
  return getSets(document).find((set) => set.foreignKey === foreignKey);
}

export function getSelectedSet<T extends SetContainer>(
  document: T,
  selectedSet?: SelectedSet
): T["documentSets"][number] {
  if (!selectedSet) {
    return null;
  }

  return isDefaultSet(selectedSet)
    ? getDefaultSet(document)
    : isSetId(selectedSet)
      ? getSetById(document, selectedSet.id)
      : isTypedSet(selectedSet)
        ? getSetByType(document, selectedSet.documentSetType)
        : getReferencedSet(document, selectedSet.foreignKey);
}

export function isSelectedSet<T extends DocumentSetModel>(set: T, selectedSet: SelectedSet): boolean {
  if (!selectedSet) {
    return false;
  }

  return isDefaultSet(selectedSet)
    ? set.isDefaultSet
    : isSetId(selectedSet)
      ? set.id === selectedSet.id
      : isTypedSet(selectedSet)
        ? selectedSet.documentSetType === set.type
        : selectedSet.foreignKey === set.foreignKey;
}

export function getSetLabel<T extends DocumentSetModel>(documentSet: T, translate?: (key: string) => string): string {
  if (!documentSet) {
    return null;
  }

  return documentSet.type
    ? translate
      ? translate(`documentSet.types.${documentSet.type}`)
      : documentSet.type
    : documentSet.name;
}

export function getSet<T extends DocumentFileModel, U extends SetContainer>(
  file: T,
  document: U
): U["documentSets"][number] {
  return getSets(document).find((set) => getSetFiles(set).includes(file));
}

export function getSetByBlob<T extends SetContainer>(document: T, blob: string): T["documentSets"][number] | nil {
  return getSets(document).find((set) => getSetFiles(set).some((file) => file.blob === blob));
}

export function hasFiles<T extends SetContainer>(document: T): boolean {
  return getFiles(document)?.length > 0;
}

export function getFiles<T extends SetContainer>(document: T): T["documentSets"][number]["files"] {
  return getSets(document).flatMap((set) => getSetFiles(set));
}

export function mapSetFiles<T extends SetContainer>(
  document: T,
  mapFile: (file: T["documentSets"][number]["files"][number]) => T["documentSets"][number]["files"][number]
): T["documentSets"] {
  return getSets(document).map((set) => ({ ...set, files: getSetFiles(set).map(mapFile) }));
}

export function getFreeFormatFiles<T extends FreeFormatFilesContainer>(document: T): T["freeFormatFiles"] {
  return document?.freeFormatFiles ?? [];
}

export function getTags<T extends DocumentFileModel>(file: T): string[] {
  return file.tags ?? [];
}

export function isDocumentFile(file: DocumentFileModel | File): file is DocumentFileModel {
  return typeof (file as File).arrayBuffer !== "function";
}

export function isRegularFile(file: DocumentFileModel | File): file is File {
  return typeof (file as File).arrayBuffer === "function";
}

export function isInternalDocument<T extends DocumentAccessData>(document?: T): boolean {
  if (document === null || document === undefined) {
    return false;
  }

  return document.isInternalDocument === null || document.isInternalDocument === undefined
    ? document.isInternalByRequirement
    : document.isInternalDocument;
}

export function filterDocumentFormats<T extends IStaticDataModel>(options: T[], availableTags: string[]): T[] {
  return options?.filter((x) => !availableTags?.length || availableTags.includes(x.value));
}

export function getDocumentTypesUtilizingTypedSets(): DocumentType[] {
  return keys(DocumentSetSettings)
    .filter<DocumentType>((type: string): type is DocumentType => !!type)
    .filter((key) => hasDeclaredDocumentSetTypes(key));
}

export function getDocumentSetTypes(fileType?: string): DocumentSetTypes | number {
  return fileType
    ? (DocumentSetSettings[fileType as TypedSetDocumentType] ?? Number.MAX_SAFE_INTEGER)
    : Number.MAX_SAFE_INTEGER;
}

export function getMaxDocumentSetCount(fileType?: string): number {
  const types = getDocumentSetTypes(fileType);
  const declared = isNumber(types) ? types : types.length;
  const restriction = getDocumentTypeUploadRestriction(fileType as DocumentType);
  return !isNil(restriction?.maxDocumentSets) ? Math.min(declared, restriction.maxDocumentSets) : declared;
}

export function declaredDocumentSetTypes(fileType?: string): readonly DocumentSetType[] {
  const types = getDocumentSetTypes(fileType);
  return isNumber(types) ? null : types;
}

export function hasDeclaredDocumentSetTypes(fileType?: string): boolean {
  return !isNil(declaredDocumentSetTypes(fileType));
}

export function declaredDocumentSetGroups(fileType?: string): DocumentSetGroupArray {
  return DocumentSetGroupsSettings[fileType as TypedSetDocumentType] ?? null;
}

export function hasDocumentSetGroups(fileType?: string): boolean {
  return !isNil(declaredDocumentSetGroups(fileType));
}

export function getDocumentSetSyncConfig(fileType?: string): DocumentSetSyncConfig[] {
  return DocumentTypeSynchronizedSetFields[fileType as CombinedDocumentContractType] ?? [];
}

export function isSystemManagedDocumentType(fileType?: string): boolean {
  return SystemManagedDocumentTypes[fileType as DocumentType] ?? false;
}

export function getDocumentTypeUploadRestriction(fileType?: string): DocumentTypeUploadRestriction | nil {
  return DocumentTypeUploadRestrictions[fileType as DocumentType] ?? null;
}

export function getEffectiveAvailableTags(fileType?: string, availableTags?: string[]): string[] {
  const safeAvailableTags = availableTags ?? [];
  const restriction = getDocumentTypeUploadRestriction(fileType);
  const allowedTags = restriction?.allowedTags ?? [];
  return isEmpty(allowedTags) || isEmpty(safeAvailableTags)
    ? safeAvailableTags
    : safeAvailableTags.filter((tag) => allowedTags.includes(tag));
}

function isNumber(value: DocumentSetTypes | number): value is number {
  return typeof value === "number";
}

export function getTargetSet<T extends SetContainer>(
  document: T,
  documentSetType: DocumentSetType | nil
): T["documentSets"][number] {
  return isNil(documentSetType) ? getDefaultSet(document) : getSetByType(document, documentSetType);
}

export function hasTaggedFile<T extends SetContainer>(
  document: T | nil,
  documentSetType: DocumentSetType | nil,
  documentTag: string
): boolean {
  if (isNil(document)) {
    return false;
  }

  const targetSet = getTargetSet(document, documentSetType);
  const hasTag = !isNil(targetSet) && getSetFiles(targetSet).some((file) => getTags(file).includes(documentTag));
  return hasTag;
}

export function getFilesWithoutTag<T extends DocumentSetModel>(set: T, documentTag: string): T["files"] {
  return getSetFiles(set).filter((file) => !getTags(file).includes(documentTag));
}
